import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import BurnRateChart from "@/components/BurnRateChart";
import PurchaseOrders from "@/components/PurchaseOrders";
import MilestonesPanel from "@/pages/mission/MilestonesPanel";
import ProfitabilityWidget from "@/components/ProfitabilityWidget";
import { useParams } from "react-router-dom";
import { flagEmoji } from "@/lib/countries";
import Flag from "@/components/Flag";
import ContactPicker from "@/components/ContactPicker";
import IconAction from "@/components/IconAction";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, CheckCircle2, UserPlus, X, Pencil, Eye, Unlock, Lock, AlertTriangle, ExternalLink, History } from "lucide-react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import ViewCraDialog from "./mission_detail/ViewCraDialog";
import { EditRoleDialog, EditAssignmentDialog } from "./mission_detail/RoleAssignmentDialogs";
import MissionCloseDialog from "./mission_detail/MissionCloseDialog";

export default function MissionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [consumption, setConsumption] = useState({ lines: [] });
  // Iter-140: realtime auto-invoice status for the current calendar month.
  const [aiPreview, setAiPreview] = useState(null);
  const [viewCra, setViewCra] = useState(null); // {user_id, user_name, year, month}
  const [craData, setCraData] = useState(null);
  // Iter-194m — close confirmation dialog with KPIs report + blocking CRAs.
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", budget_jh: "", tjm: "" });
  const [assignTo, setAssignTo] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [roleEdit, setRoleEdit] = useState(null); // {role_id, name, budget_jh, tjm}
  const [assignEdit, setAssignEdit] = useState(null); // {role_id, user_id, user_name, budget_jh, start_date}
  const [alerts, setAlerts] = useState([]);
  const [poMismatch, setPoMismatch] = useState(null);
  const [auditLog, setAuditLog] = useState(null);  // null = not loaded; [] = loaded (empty ok); false = forbidden
  const [assignMgrOpen, setAssignMgrOpen] = useState(false);
  const [assignMgrPick, setAssignMgrPick] = useState("");
  const [assignableManagers, setAssignableManagers] = useState([]);
  const [countries, setCountries] = useState([]);
  // Iter-56: drive folder URL prompt at validation time (CEO only, when URL is missing).
  const [driveValidateOpen, setDriveValidateOpen] = useState(false);
  const [driveValidateUrl, setDriveValidateUrl] = useState("");
  const [driveValidateSaving, setDriveValidateSaving] = useState(false);

  useEffect(() => {
    api.get("/holidays/countries").then((r) => setCountries(r.data)).catch(() => setCountries([]));
    // Iter-60: load assignable managers for the Edit Mission dialog (CEO/ADMIN only).
    if (user.role === "CEO" || user.role === "ADMIN") {
      api.get("/users/assignable-managers").then((r) => setAssignableManagers(r.data || [])).catch(() => setAssignableManagers([]));
    }
  }, []);

  const canViewAudit = user.role === "CEO" || (user.permissions || []).includes("view_mission_audit_log");
  // Iter-208 — Financial-data gates. `canViewFinance` controls TJM/total HT,
  // forfait milestones (amounts), profitability widget, close-report. `canViewPO`
  // controls the Bons de commande tab. Both are *permission* driven so they
  // remain consistent with the Role Permissions matrix and custom roles.
  const canViewFinance = user.role === "CEO" || (user.permissions || []).includes("view_mission_financials");
  const canViewPO = user.role === "CEO" || (user.permissions || []).includes("view_purchase_orders") || (user.permissions || []).includes("manage_purchase_orders");

  const load = async () => {
    const [mm, uu, cc, cons] = await Promise.all([
      api.get(`/missions/${id}`),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get(`/missions/${id}/consumption`).catch(() => ({ data: { lines: [] } })),
    ]);
    setM(mm.data);
    setUsers(uu.data);
    setClients(cc.data);
    setConsumption(cons.data);
    // Iter-140: load auto-invoice preview for this mission × current month so we can
    // surface the realtime status badge in the header.
    const today = new Date();
    try {
      const r = await api.get(`/billing/auto-invoice/preview/${id}?year=${today.getFullYear()}&month=${today.getMonth() + 1}`);
      setAiPreview(r.data);
    } catch (_e) { setAiPreview(null); }
  };
  useEffect(() => { load(); }, [id]);

  const loadPoMismatch = async () => {
    try {
      const r = await api.get(`/missions/${id}/purchase-orders-mismatch`);
      setPoMismatch(r.data);
    } catch {
      setPoMismatch(null);
    }
  };

  const loadAuditLog = async () => {
    if (!canViewAudit) return;
    try {
      const r = await api.get(`/missions/${id}/audit-log`);
      setAuditLog(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setAuditLog(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    api.get(`/missions/${id}/alerts`).then((r) => setAlerts(r.data)).catch(() => {});
    loadPoMismatch();
  }, [id, consumption]);

  if (!m) return <div className="p-12 text-zinc-500">Loading…</div>;

  const canEdit = (user.role === "MANAGER" && m.manager_id === user.user_id) || user.role === "CEO";
  const canAssignManager = user.role === "CEO" || user.role === "ADMIN";
  const missionHasNoManager = !m.manager_id;
  const canValidate = user.role === "CEO" || (user.permissions || []).includes("validate_mission");
  const isPending = m.status === "PENDING_VALIDATION";
  const isDraft = m.status === "DRAFT";
  const isOwner = m.manager_id === user.user_id;
  const totalAssigned = m.roles.reduce((a, r) => a + r.assignments.reduce((x, y) => x + y.budget_jh, 0), 0);
  const remaining = m.budget_jh - totalAssigned;

  const saveAssignManager = async () => {
    if (!assignMgrPick) return toast.error("Select a manager");
    try {
      await api.post(`/missions/${id}/assign-manager?manager_id=${assignMgrPick}`);
      toast.success("Manager assigned");
      setAssignMgrOpen(false);
      setAssignMgrPick("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const managerName = m.manager_id ? (users.find((x) => x.user_id === m.manager_id)?.name || m.manager_id) : null;

  const addRole = async () => {
    if (!newRole.name || !newRole.budget_jh) return toast.error("Fill name and JH");
    if (m.type !== "FORFAIT" && !newRole.tjm) return toast.error("Fill TJM (required for Régie)");
    try {
      await api.post(`/missions/${id}/roles`, {
        name: newRole.name,
        budget_jh: parseFloat(newRole.budget_jh),
        tjm: m.type === "FORFAIT" ? 0 : parseFloat(newRole.tjm),
      });
      setNewRole({ name: "", budget_jh: "", tjm: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const assign = async (role_id) => {
    const local = assignTo[role_id] || {};
    if (!local.user_id || !local.budget_jh || !local.start_date) return toast.error("Select user, MD and date");
    try {
      await api.post(`/missions/${id}/assignments`, {
        role_id, user_id: local.user_id,
        budget_jh: parseFloat(local.budget_jh), start_date: local.start_date,
      });
      setAssignTo({ ...assignTo, [role_id]: {} });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assignment failed"); }
  };

  const unassign = async (role_id, user_id) => {
    await api.delete(`/missions/${id}/assignments?role_id=${role_id}&user_id=${user_id}`);
    load();
  };

  const _doValidate = async () => {
    try {
      const r = await api.post(`/missions/${id}/validate`);
      const newStatus = r.data?.status;
      if (newStatus === "PENDING_VALIDATION") {
        toast.success("Mission submitted for validation. The CEO has been notified.");
      } else if (newStatus === "ACTIVE") {
        toast.success("Mission activated. Collaborators can now log their hours.");
      } else {
        toast.success("Mission updated");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const validate = async () => {
    // Iter-125: BDC drive folder is now derived from client.drive_subfolders.bdc.
    // No prompt needed — go straight to validation.
    await _doValidate();
  };

  const confirmDriveAndValidate = async () => {
    setDriveValidateSaving(true);
    try {
      const url = (driveValidateUrl || "").trim();
      if (url) {
        await api.patch(`/missions/${id}`, { drive_folder_url: url });
      }
      setDriveValidateOpen(false);
      await _doValidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setDriveValidateSaving(false);
    }
  };

  const skipDriveAndValidate = async () => {
    setDriveValidateOpen(false);
    await _doValidate();
  };

  const removeRole = async (role_id) => {
    try {
      await api.delete(`/missions/${id}/roles/${role_id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openRoleEdit = (r) => setRoleEdit({ role_id: r.role_id, name: r.name, budget_jh: r.budget_jh, tjm: r.tjm });
  const saveRoleEdit = async () => {
    if (!roleEdit) return;
    try {
      const payload = {
        name: roleEdit.name,
        budget_jh: parseFloat(roleEdit.budget_jh),
      };
      if (m.type !== "FORFAIT") {
        payload.tjm = parseFloat(roleEdit.tjm);
      }
      await api.patch(`/missions/${id}/roles/${roleEdit.role_id}`, payload);
      toast.success("Role updated");
      setRoleEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openAssignEdit = (role_id, a) => setAssignEdit({
    role_id, user_id: a.user_id, user_name: userName(a.user_id),
    budget_jh: a.budget_jh, start_date: a.start_date,
  });
  const saveAssignEdit = async () => {
    if (!assignEdit) return;
    try {
      await api.patch(`/missions/${id}/assignments`, {
        role_id: assignEdit.role_id,
        user_id: assignEdit.user_id,
        budget_jh: parseFloat(assignEdit.budget_jh),
        start_date: assignEdit.start_date,
      });
      toast.success("Assignment updated");
      setAssignEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openEdit = () => {
    setEditForm({
      title: m.title, description: m.description || "",
      start_date: m.start_date, end_date: m.end_date,
      budget_jh: m.budget_jh, client_id: m.client_id,
      country: m.country || "",
      contact_name: m.contact_name || "",
      contact_email: m.contact_email || "",
      contact_gender: m.contact_gender || "",
      final_client_name: m.final_client_name || "",
      manager_id: m.manager_id || "",  // iter-60: CEO/ADMIN can reassign manager from edit dialog
      // Iter-137: tri-state auto-invoice — null = inherit client/global; true/false = override
      auto_invoice_enabled: m.auto_invoice_enabled ?? null,
      // Iter-195q — tri-state send mode (null = inherit; END_OF_MONTH | IMMEDIATE = override)
      auto_invoice_send_mode: m.auto_invoice_send_mode ?? null,
      // Iter-191 — Forfait-only fields
      bdc_only: !!m.bdc_only,
      total_amount_ht: m.total_amount_ht ?? "",
      // Iter-194j/w — Billing delay (months) and optional days override.
      billing_delay_months: m.billing_delay_months || 1,
      billing_delay_days: m.billing_delay_days ?? "",
      // Iter-203 — Weekly CRA opt-in.
      weekly_cra_enabled: !!m.weekly_cra_enabled,
      // Iter-204 — `type` becomes editable after creation until the first invoice is sent.
      type: m.type || "REGIE",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const wantsManagerChange = (user.role === "CEO" || user.role === "ADMIN")
        && editForm.manager_id
        && editForm.manager_id !== m.manager_id;
      const { manager_id: _ignored, ...patch } = editForm;
      // Iter-204 — strip `type` from the payload when locked OR when unchanged.
      // The backend would 409 on a locked type-change attempt, but stripping early
      // keeps the save path clean (no-op when the value matches the current type).
      if (m.type_locked || patch.type === m.type) {
        delete patch.type;
      }
      // Iter-203/204 — Weekly CRA is REGIE-only. The effective post-PATCH type drives
      // whether the field is allowed; if the user is switching to FORFAIT or the
      // mission is already FORFAIT, force false here as a UI-side guard (the backend
      // double-checks).
      const effType = patch.type || m.type;
      if (effType === "FORFAIT") {
        patch.weekly_cra_enabled = false;
      }
      // Iter-191 — strip FORFAIT-only fields when the EFFECTIVE type is not Forfait.
      if (effType !== "FORFAIT") {
        delete patch.bdc_only;
        delete patch.total_amount_ht;
      } else if (patch.total_amount_ht !== "" && patch.total_amount_ht != null) {
        patch.total_amount_ht = parseFloat(patch.total_amount_ht);
      } else {
        delete patch.total_amount_ht;
      }
      // Iter-194j/w — Billing delay (months + optional days override).
      // Backend gates these to CEO only — strip them for everyone else so a
      // manager edit doesn't 403 the entire save.
      if (user.role !== "CEO") {
        delete patch.billing_delay_months;
        delete patch.billing_delay_days;
      } else {
        if (patch.billing_delay_months != null && patch.billing_delay_months !== "") {
          const bdm = parseInt(patch.billing_delay_months, 10);
          patch.billing_delay_months = [1, 2, 3].includes(bdm) ? bdm : 1;
        } else {
          delete patch.billing_delay_months;
        }
        if (patch.billing_delay_days === "" || patch.billing_delay_days == null) {
          patch.billing_delay_days = null;
        } else {
          const bdd = parseInt(patch.billing_delay_days, 10);
          if (Number.isNaN(bdd) || bdd < 1 || bdd > 365) {
            toast.error("Override en jours: doit être un entier entre 1 et 365");
            return;
          }
          patch.billing_delay_days = bdd;
        }
      }
      await api.patch(`/missions/${id}`, {
        ...patch,
        budget_jh: parseFloat(patch.budget_jh),
      });
      // Iter-60: manager reassignment goes through the dedicated endpoint (it logs an event,
      // notifies the new manager, and runs role validation server-side).
      if (wantsManagerChange) {
        await api.post(`/missions/${id}/assign-manager?manager_id=${editForm.manager_id}`);
      }
      toast.success("Mission updated");
      setEditOpen(false);
      load();
    } catch (e) {
      // Iter-204 — surface the backend's `mission_type_locked` detail object,
      // which is `{code, message}`. Fallback to generic detail otherwise.
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message)
        ? detail.message
        : (typeof detail === "string" ? detail : "Error");
      toast.error(msg);
    }
  };

  const openCra = async (a) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    setViewCra({ user_id: a.user_id, user_name: userName(a.user_id), year, month });
    try {
      const r = await api.get(`/cra/${year}/${month}`, { params: { user_id: a.user_id } });
      setCraData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot load CRA");
    }
  };

  const changeCraMonth = async (year, month) => {
    setViewCra({ ...viewCra, year, month });
    const r = await api.get(`/cra/${year}/${month}`, { params: { user_id: viewCra.user_id } });
    setCraData(r.data);
  };

  const unlockCra = async (userId, year, month) => {
    try {
      await api.post(`/missions/${id}/collaborators/${userId}/unlock`, null, { params: { year, month } });
      toast.success("CRA unlocked for editing");
      if (viewCra && viewCra.user_id === userId && viewCra.year === year && viewCra.month === month) {
        changeCraMonth(year, month);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Unlock failed"); }
  };

  const userName = (uid) => users.find((x) => x.user_id === uid)?.name || uid;
  const userPic = (uid) => users.find((x) => x.user_id === uid)?.picture;
  const alertFor = (userId) => {
    const a = alerts.filter((x) => x.target === userId).sort((x, y) => y.threshold - x.threshold)[0];
    return a?.threshold || 0;
  };
  const missionAlert = alerts.filter((x) => x.target === "MISSION").sort((x, y) => y.threshold - x.threshold)[0]?.threshold || 0;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="mission-detail">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 md:mb-10 gap-4 md:gap-8">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-3">
            <span className="w-3 h-3 rounded-full" style={{ background: m.color }} />
            {m.type} · {m.currency} · {m.status}
            {m.country && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-brand-cream text-brand-navy font-mono rounded tracking-normal normal-case" data-testid="mission-country-badge">
                <Flag code={m.country} size={14} alt={false} />{m.country}
              </span>
            )}
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-3">{m.title}</h1>
          {/* Iter-140: realtime auto-invoice badge (current month) */}
          {(user.role === "CEO" || user.role === "ADMIN") && aiPreview && (() => {
            const { would_run, cra_check, guard, enabled_cascade, is_subcontractor, subcontractor_status, shadow_mode } = aiPreview;
            // Iter-196f — replaced emoji status dots (🟢 🔴 🟡 ⚪) with a small
            // CSS-rendered circle so the badge renders reliably on Android.
            let tone = "zinc", label = "Auto-invoice off";
            if (!enabled_cascade?.effective) {
              tone = "zinc"; label = "Auto-invoice disabled";
            } else if (would_run) {
              tone = "emerald"; label = shadow_mode ? "Ready (shadow)" : "Ready to run";
            } else if (guard) {
              tone = "red"; label = `Blocked: ${guard.label}`;
            } else if (cra_check && !cra_check.ok) {
              const pending = cra_check.pending?.length || 0;
              tone = "amber"; label = `Waiting: ${pending} CRA non approuvé(s)`;
            } else if (is_subcontractor && subcontractor_status?.code !== "UNLOCKED") {
              tone = "amber"; label = `Sub-traitant: ${subcontractor_status?.label || "en attente"}`;
            }
            const colorMap = {
              emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
              amber: "bg-amber-50 border-amber-200 text-amber-800",
              red: "bg-red-50 border-red-200 text-red-800",
              zinc: "bg-zinc-50 border-zinc-200 text-zinc-700",
            };
            const dotMap = {
              emerald: "bg-emerald-500",
              amber: "bg-amber-500",
              red: "bg-red-500",
              zinc: "bg-zinc-400",
            };
            return (
              <div data-testid="mission-auto-invoice-badge" className={`inline-flex items-center gap-2 mb-3 px-3 py-1.5 text-[12px] font-mono rounded-md border ${colorMap[tone]}`}>
                <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${dotMap[tone]}`} aria-hidden="true" />
                <span>{m.type === "FORFAIT" ? "Auto-invoice (validation palier) :" : "Auto-invoice (mois en cours) :"}</span>
                <span className="font-semibold">{label}</span>
              </div>
            );
          })()}
          <p className="text-zinc-600 max-w-2xl">{m.description}</p>
          <div className="mt-3 text-xs font-mono text-zinc-500" data-testid="mission-manager-info">
            Manager : {managerName ? <span className="text-brand-navy font-semibold">{managerName}</span> : <span className="text-amber-700 font-semibold">Non assigned</span>}
          </div>
          {m.drive_folder_url && (
            <a
              href={m.drive_folder_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mission-drive-link"
              className="inline-flex items-center gap-2 mt-3 text-sm text-blue-700 hover:text-blue-900 hover:underline"
            >
              <ExternalLink size={14} /> Dossier Google Drive partagé
            </a>
          )}
          {user.role === "CEO" && (
            <CeoDriveFolderEditor
              missionId={id}
              initialUrl={m.drive_folder_url || ""}
              onSaved={load}
            />
          )}
          {/* Iter-228w — per-mission bank account override (CEO only). */}
          {user.role === "CEO" && (
            <MissionBankAccountOverride
              missionId={id}
              currency={m.currency || "MAD"}
              currentValue={m.bank_account_id || ""}
              onSaved={load}
            />
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1.5 shrink-0" data-testid="mission-actions">
            <IconAction icon={Pencil} size="lg" testid="edit-mission-btn" label="Modifier la mission" onClick={openEdit} />
            {isDraft && isOwner && !canValidate && (
              <IconAction icon={CheckCircle2} size="lg" tone="dark" testid="submit-mission-btn" label="Soumettre pour validation" onClick={validate} />
            )}
            {isDraft && canValidate && (
              <IconAction icon={CheckCircle2} size="lg" tone="dark" testid="validate-mission-btn" label="Valider et activer la mission" onClick={validate} />
            )}
            {isPending && canValidate && (
              <IconAction icon={CheckCircle2} size="lg" tone="success" testid="approve-mission-btn" label="Approuver la mission" onClick={validate} />
            )}
            {isPending && !canValidate && (
              <div className="inline-flex items-center gap-2 px-3 h-10 bg-amber-50 text-amber-900 text-xs rounded-md border border-amber-200" data-testid="pending-validation-badge">
                <AlertTriangle size={14} /> En attente de validation CEO
              </div>
            )}
            {m.status === "ACTIVE" && (
              <IconAction icon={Lock} size="lg" testid="close-mission-btn" label="Clôturer la mission" onClick={() => setCloseDialogOpen(true)} />
            )}
            {m.status === "CLOSED" && (
              <IconAction icon={Unlock} size="lg" testid="reopen-mission-btn" label="Réouvrir la mission" onClick={async () => {
                try {
                  await api.post(`/missions/${id}/reopen`);
                  toast.success("Mission reactivated");
                  load();
                } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
              }} />
            )}
          </div>
        )}
      </div>

      {/* Manager assignment banner — mission created by Gestionnaire and not yet assigned */}
      {missionHasNoManager && (
        <div
          data-testid="no-manager-banner"
          className="mb-6 flex items-center justify-between gap-3 px-5 py-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg"
        >
          <div className="flex items-center gap-3 text-sm">
            <AlertTriangle size={16} className="shrink-0" />
            <span>
              <strong>Manager non assigned.</strong> Cette mission a été created sans manager — l'attribution par le CEO est requise avant activation.
            </span>
          </div>
          {canAssignManager && (
            <button
              data-testid="open-assign-manager-btn"
              onClick={() => setAssignMgrOpen(true)}
              className="inline-flex items-center gap-2 px-4 h-9 bg-brand-navy text-white text-xs font-semibold rounded-md hover:bg-brand-navy-600"
            >
              <UserPlus size={13} /> Attribuer un manager
            </button>
          )}
        </div>
      )}

      {/* PO mismatch banner — visible across all tabs */}
      {poMismatch?.has_mismatch && (
        <div className="mb-6 flex items-center gap-3 px-5 py-4 bg-red-50 border border-red-200 text-red-900 rounded-lg" data-testid="po-mismatch-banner">
          <AlertTriangle size={16} className="shrink-0" />
          <div className="text-sm">
            {m.type === "FORFAIT" && poMismatch?.forfait_mismatch?.has_mismatch ? (
              poMismatch.forfait_mismatch.reason === "no_po" ? (
                <span data-testid="forfait-po-missing-banner">
                  <strong>Aucun bon de commande</strong> n'a été déposé pour cette mission Forfait. Le total HT attendu est de <strong>{poMismatch.forfait_mismatch.mission_total_ht} {m.currency}</strong>.
                </span>
              ) : (
                <span data-testid="forfait-po-amount-mismatch-banner">
                  Le total des bons de commande (<strong>{poMismatch.forfait_mismatch.po_total_ht} {m.currency}</strong>) ne correspond pas au montant total HT déclaré sur la mission Forfait (<strong>{poMismatch.forfait_mismatch.mission_total_ht} {m.currency}</strong>).
                </span>
              )
            ) : (
              <span>
                Un ou plusieurs <strong>bons de commande</strong> présentent un déphasage avec la configuration de la mission (rôle / TJM / JH).
                Consultez l'onglet <em>Bons de commande</em> pour corriger.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Iter-190 — Profitability widget (CEO/GESTIONNAIRE: full; Manager: aggregate) */}
      {canViewFinance && (
        <ProfitabilityWidget missionId={id} refreshKey={(m.roles || []).reduce((s, r) => s + (r.assignments?.length || 0), 0)} />
      )}

      {/* Budget bar — JH envelope only for Régie missions (Forfait uses milestones) */}
      {m.type !== "FORFAIT" && (
      <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono flex items-center gap-2">
            Envelope
            {missionAlert >= 100 && (
              <span className="inline-flex items-center text-[10px] px-2 py-0.5 bg-red-100 text-red-800 rounded-full font-mono normal-case tracking-normal" data-testid="mission-alert-100">⚠ Mission 100% reached</span>
            )}
            {missionAlert >= 80 && missionAlert < 100 && (
              <span className="inline-flex items-center text-[10px] px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-mono normal-case tracking-normal" data-testid="mission-alert-80">⚠ Mission 80% reached</span>
            )}
          </div>
          <div className="text-xs font-mono text-zinc-600">{totalAssigned} / {m.budget_jh} JH assigned · {remaining} remaining</div>
        </div>
        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden flex">
          {m.roles.map((r) => {
            const assigned = r.assignments.reduce((a, x) => a + x.budget_jh, 0);
            return <div key={r.role_id} style={{ width: `${(assigned / m.budget_jh) * 100}%`, background: m.color, opacity: 0.85 }} />;
          })}
        </div>
      </div>
      )}

      {/* Iter-188 — Forfait JH envelope (auto-derived from roles, not user-set) */}
      {m.type === "FORFAIT" && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" data-testid="forfait-envelope-block">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">JH Envelope (auto)</div>
            <div className="text-xs font-mono text-zinc-600">
              <span data-testid="forfait-envelope-total">{m.budget_jh || 0} JH</span> total
              {" · "}
              <span data-testid="forfait-envelope-assigned">{totalAssigned} JH</span> staffed
              {(m.budget_jh || 0) > 0 && (
                <span className="text-zinc-400"> · {Math.max(0, (m.budget_jh || 0) - totalAssigned)} JH remaining</span>
              )}
            </div>
          </div>
          {(m.budget_jh || 0) > 0 ? (
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden flex">
              {m.roles.map((r) => {
                const assigned = r.assignments.reduce((a, x) => a + x.budget_jh, 0);
                return <div key={r.role_id} style={{ width: `${(assigned / m.budget_jh) * 100}%`, background: m.color, opacity: 0.85 }} />;
              })}
            </div>
          ) : (
            <div className="text-xs text-zinc-400 italic">No roles defined yet — the envelope grows as you add roles in Staffing.</div>
          )}
          <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
            For Forfait missions, the JH envelope is the sum of role budgets — it is updated automatically when roles are added, edited or removed.
          </div>
        </div>
      )}

      {/* Burn-rate projection */}
      <div className="mb-8">
        <BurnRateChart missionId={id} />
      </div>

      <Tabs defaultValue="staffing" className="w-full">
        <div className="overflow-x-auto -mx-2 px-2 mb-6">
          <TabsList data-testid="mission-tabs">
            <TabsTrigger value="staffing" data-testid="tab-staffing">Staffing & roles</TabsTrigger>
            {canViewPO && (
              <TabsTrigger value="po" data-testid="tab-po" className="relative">
                Bons de commande
                {poMismatch?.has_mismatch && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-red-600" data-testid="tab-po-dot" />}
              </TabsTrigger>
            )}
            {m.type === "FORFAIT" && canViewFinance && (
              <TabsTrigger value="milestones" data-testid="tab-milestones" className="relative">
                Paliers de facturation
                {(m.milestones || []).some((ms) => ms.status === "PENDING_VALIDATION") && (
                  <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-amber-500" data-testid="tab-milestones-dot" />
                )}
              </TabsTrigger>
            )}
            {canViewAudit && (
              <TabsTrigger value="audit" data-testid="tab-audit" onClick={loadAuditLog}>
                <History size={14} className="mr-1.5" /> Journal
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        <TabsContent value="staffing">

      {/* Roles */}
      <div className="space-y-5">
        {m.roles.map((r) => {
          const assigned = r.assignments.reduce((a, x) => a + x.budget_jh, 0);
          const pct = (assigned / r.budget_jh) * 100;
          const local = assignTo[r.role_id] || {};
          // Iter-111 / Iter-191: surface a visible "PO missing" badge on every role that the
          // backend flagged in `mission.po_missing_roles`. The badge clears the moment
          // a covering PO is uploaded with `budget_jh > 0` and the mission is reloaded.
          // FORFAIT missions intentionally do NOT use the per-role badge — the PO mismatch
          // is tracked at mission level (top banner) instead.
          const poMissing = m.type !== "FORFAIT" && Array.isArray(m.po_missing_roles) && m.po_missing_roles.includes(r.name);
          return (
            <div key={r.role_id} className="bg-white border border-zinc-200 rounded-lg p-6" data-testid={`role-${r.role_id}`}>
              <div className="flex items-start justify-between mb-4 gap-4">
                <div>
                  <div className="font-heading font-semibold text-lg tracking-tight inline-flex items-center gap-2">
                    {r.name}
                    {poMissing && (
                      <span
                        data-testid={`role-po-missing-${r.role_id}`}
                        title="Bon de commande manquant pour ce rôle. Uploader un BC avec une enveloppe > 0 pour le couvrir."
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-mono uppercase tracking-wider border border-amber-300"
                      >
                        <AlertTriangle size={10} /> BC manquant
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono mt-1">
                    {m.type === "FORFAIT" ? (
                      <span>{r.budget_jh} JH</span>
                    ) : canViewFinance ? (
                      <span>{r.budget_jh} JH · TJM {r.tjm} {m.currency} · Envelope {(r.budget_jh * r.tjm).toLocaleString()} {m.currency}</span>
                    ) : (
                      <span>{r.budget_jh} JH</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs font-mono text-zinc-600">{assigned} / {r.budget_jh} JH</div>
                  {canEdit && (
                    <IconAction icon={Pencil} testid={`edit-role-${r.role_id}`} label="Modifier le rôle" onClick={() => openRoleEdit(r)} />
                  )}
                  {canEdit && (
                    <IconAction icon={Trash2} tone="danger" testid={`del-role-${r.role_id}`} label="Supprimer le rôle" onClick={() => removeRole(r.role_id)} />
                  )}
                </div>
              </div>
              <div className="h-1 bg-zinc-100 rounded-full mb-5 overflow-hidden">
                <div className="h-full bg-zinc-950" style={{ width: `${Math.min(100, pct)}%` }} />
              </div>

              {/* Assignments */}
              <div className="space-y-2 mb-4">
                {r.assignments.length === 0 && (
                  <div className="text-sm text-zinc-500 italic">No one staffed yet.</div>
                )}
                {r.assignments.map((a) => {
                  const line = consumption.lines.find((l) => l.user_id === a.user_id && l.role_id === r.role_id);
                  const consumedJH = line?.consumed_jh ?? 0;
                  const remainingJH = line?.remaining_jh ?? a.budget_jh;
                  const overBudget = remainingJH < 0;
                  return (
                  <div key={a.user_id} className="flex items-center justify-between bg-zinc-50 border border-zinc-100 rounded-md px-4 py-3" data-testid={`assignment-${r.role_id}-${a.user_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {userPic(a.user_id) ? (
                        <img src={userPic(a.user_id)} alt="" className="w-7 h-7 rounded-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-zinc-200 text-zinc-700 flex items-center justify-center text-[11px] font-medium uppercase">
                          {(userName(a.user_id) || "").slice(0, 2)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-2">
                          {userName(a.user_id)}
                          {alertFor(a.user_id) >= 100 && (
                            <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 bg-red-100 text-red-800 rounded-full font-mono" data-testid={`alert-100-${a.user_id}`}>⚠ 100% JH</span>
                          )}
                          {alertFor(a.user_id) >= 80 && alertFor(a.user_id) < 100 && (
                            <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full font-mono" data-testid={`alert-80-${a.user_id}`}>⚠ 80% JH</span>
                          )}
                        </div>
                        <div className="text-xs font-mono text-zinc-500">
                          <span className="text-zinc-950 font-medium">{consumedJH}</span> / {a.budget_jh} JH ·
                          <span className={overBudget ? "text-red-700 font-medium ml-1" : "ml-1"}> {remainingJH} remaining</span>
                          <span className="text-zinc-400"> · from {a.start_date}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {canEdit && (
                        <IconAction icon={Pencil} testid={`edit-assign-${r.role_id}-${a.user_id}`} label="Éditer l'affectation" onClick={() => openAssignEdit(r.role_id, a)} />
                      )}
                      {canEdit && (
                        <IconAction icon={Eye} testid={`view-cra-${a.user_id}`} label="Voir le CRA du collaborateur" onClick={() => openCra(a)} />
                      )}
                      {canEdit && (
                        <IconAction icon={X} tone="danger" testid={`unassign-${r.role_id}-${a.user_id}`} label="Retirer l'affectation" onClick={() => unassign(r.role_id, a.user_id)} />
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>

              {canEdit && (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_160px_auto] gap-3 pt-3 border-t border-zinc-100">
                  <Select
                    value={local.user_id || ""}
                    onValueChange={(v) => setAssignTo({ ...assignTo, [r.role_id]: { ...local, user_id: v } })}
                  >
                    <SelectTrigger data-testid={`assign-user-${r.role_id}`}><SelectValue placeholder="Pick collaborator" /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          {u.name} — <span className="text-xs text-zinc-500">{u.email}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid={`assign-jh-${r.role_id}`}
                    type="number" step="0.5" min="0" placeholder="JH"
                    value={local.budget_jh || ""}
                    onChange={(e) => setAssignTo({ ...assignTo, [r.role_id]: { ...local, budget_jh: e.target.value } })}
                  />
                  <Input
                    data-testid={`assign-date-${r.role_id}`}
                    type="date"
                    value={local.start_date || ""}
                    onChange={(e) => setAssignTo({ ...assignTo, [r.role_id]: { ...local, start_date: e.target.value } })}
                  />
                  <button onClick={() => assign(r.role_id)} data-testid={`assign-submit-${r.role_id}`} className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
                    <UserPlus size={14} /> Staff
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="mt-8 bg-white border border-dashed border-zinc-300 rounded-lg p-6">
          <div className="font-heading font-semibold mb-4">Add a role</div>
          <div className={`grid grid-cols-1 ${(m.type === "FORFAIT" || !canViewFinance) ? "md:grid-cols-[1fr_120px_auto]" : "md:grid-cols-[1fr_120px_140px_auto]"} gap-3`}>
            <Input data-testid="newrole-name" placeholder="e.g. Senior Consultant" value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} />
            <Input data-testid="newrole-jh" type="number" step="0.5" placeholder="JH" value={newRole.budget_jh} onChange={(e) => setNewRole({ ...newRole, budget_jh: e.target.value })} />
            {m.type !== "FORFAIT" && canViewFinance && (
              <Input data-testid="newrole-tjm" type="number" step="50" placeholder={`TJM ${m.currency}`} value={newRole.tjm} onChange={(e) => setNewRole({ ...newRole, tjm: e.target.value })} />
            )}
            <button onClick={addRole} data-testid="newrole-submit" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md">
              <Plus size={14} /> Add role
            </button>
          </div>
          {m.type === "FORFAIT" && (
            <p className="text-[11px] text-zinc-500 mt-2">TJM is not required on Forfait roles — the mission is billed on the fixed total amount.</p>
          )}
        </div>
      )}
        </TabsContent>
        {canViewPO && (
          <TabsContent value="po">
            <PurchaseOrders missionId={id} mission={m} onChange={loadPoMismatch} />
          </TabsContent>
        )}
        {m.type === "FORFAIT" && canViewFinance && (
          <TabsContent value="milestones">
            <MilestonesPanel mission={m} onChange={load} />
          </TabsContent>
        )}
        {canViewAudit && (
          <TabsContent value="audit">
            <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="audit-log-panel">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-heading font-semibold text-base">Journal des modifications</div>
                  <div className="text-xs text-zinc-500 font-mono mt-0.5">Historique sur les 365 derniers jours</div>
                </div>
                <button onClick={loadAuditLog} className="text-xs text-zinc-600 hover:text-zinc-900">Refresh</button>
              </div>
              {auditLog === null && <div className="text-sm text-zinc-500 italic">Loading…</div>}
              {auditLog === false && <div className="text-sm text-zinc-500 italic">Access denied.</div>}
              {Array.isArray(auditLog) && auditLog.length === 0 && <div className="text-sm text-zinc-500 italic">No change saved.</div>}
              {Array.isArray(auditLog) && auditLog.length > 0 && (
                <div className="divide-y divide-zinc-100 text-sm">
                  {auditLog.map((e) => {
                    // Iter-204b — special-case display for the type-change event so
                    // accountants can spot REGIE↔FORFAIT transitions instantly.
                    const isTypeChange = e.action === "MISSION_TYPE_CHANGED";
                    const d = e.details || {};
                    const wcraFlip = isTypeChange && d.weekly_cra_enabled_before !== d.weekly_cra_enabled_after;
                    return (
                    <div key={e.log_id} className="py-3 flex items-start gap-4" data-testid={`audit-entry-${e.log_id}`}>
                      <div className="font-mono text-[11px] text-zinc-500 w-44 shrink-0">{new Date(e.created_at).toLocaleString("fr-FR")}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] uppercase tracking-[0.2em] font-mono px-2 py-0.5 rounded ${isTypeChange ? "bg-amber-100 text-amber-900 border border-amber-200" : "bg-zinc-100"}`}>
                            {isTypeChange ? "Type modifié" : e.action}
                          </span>
                          <span className="text-xs text-zinc-600">par {e.actor_name}</span>
                        </div>
                        {isTypeChange ? (
                          <div className="mt-1.5 text-xs text-zinc-700 leading-relaxed" data-testid={`audit-type-change-${e.log_id}`}>
                            Le type a été basculé de{" "}
                            <span className="font-mono px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200 text-zinc-800">
                              {d.previous_type || "—"}
                            </span>{" "}
                            vers{" "}
                            <span className="font-mono px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-200 text-emerald-900">
                              {d.new_type || "—"}
                            </span>
                            {wcraFlip && (
                              <span className="block mt-1 text-[11px] text-amber-700">
                                ⓘ Weekly CRA passé de <strong>{d.weekly_cra_enabled_before ? "activé" : "désactivé"}</strong> à <strong>{d.weekly_cra_enabled_after ? "activé" : "désactivé"}</strong> automatiquement.
                              </span>
                            )}
                          </div>
                        ) : (
                          <pre className="mt-1 text-[11px] font-mono text-zinc-700 bg-zinc-50 border border-zinc-100 rounded px-2 py-1 whitespace-pre-wrap break-words">{JSON.stringify(e.details, null, 0)}</pre>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <ViewCraDialog
        open={!!viewCra}
        viewCra={viewCra}
        craData={craData}
        missionId={id}
        canEdit={canEdit}
        onClose={() => { setViewCra(null); setCraData(null); }}
        onChangeMonth={changeCraMonth}
        onUnlock={unlockCra}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-mission-dialog" className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 bg-brand-paper">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-brand-cream-200 shrink-0 bg-brand-paper">
            <DialogTitle>Edit mission</DialogTitle>
            <DialogDescription>
              All fields marked with <span className="text-red-600 font-bold">*</span> are required. The new budget must be ≥ currently assigned JH.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1 min-h-0">
            <div><Label>Title <span className="text-red-600">*</span></Label><Input data-testid="edit-field-title" value={editForm.title || ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
            <div><Label>Description <span className="text-red-600">*</span></Label><Textarea data-testid="edit-field-description" value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            <div>
              <Label>Client <span className="text-red-600">*</span></Label>
              <Select value={editForm.client_id || ""} onValueChange={(v) => setEditForm({ ...editForm, client_id: v })}>
                <SelectTrigger data-testid="edit-field-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Iter-204 — Type selector. Editable until the first invoice is sent.
                Once locked, the field is disabled with a clear FR explanation so
                CEO/Manager understand WHY they can't switch (audit trail / billing
                consistency). */}
            <div data-testid="edit-mission-type-block">
              <Label className="flex items-center gap-2">
                Type
                {m.type_locked && <Lock size={12} className="text-zinc-500" />}
                <span className="text-red-600">*</span>
              </Label>
              <Select
                value={editForm.type || m.type || "REGIE"}
                disabled={!!m.type_locked}
                onValueChange={(v) => setEditForm({
                  ...editForm,
                  type: v,
                  // Iter-204 cascade — switching to FORFAIT auto-disables Weekly CRA.
                  ...(v === "FORFAIT" ? { weekly_cra_enabled: false } : {}),
                })}
              >
                <SelectTrigger data-testid="edit-field-type" className={m.type_locked ? "opacity-60 cursor-not-allowed" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="REGIE">Régie</SelectItem>
                  <SelectItem value="FORFAIT">Forfait</SelectItem>
                </SelectContent>
              </Select>
              {m.type_locked ? (
                <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed" data-testid="edit-mission-type-locked-note">
                  <Lock size={10} className="inline-block mr-1 -mt-0.5" />
                  Verrouillé — {m.type_locked_sent_invoices || 0} facture(s) déjà émise(s) ou envoyée(s).
                  Le type ne peut plus être modifié pour préserver la cohérence comptable.
                </p>
              ) : (
                <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  Modifiable tant qu'aucune facture n'a été émise. Changer le type ajuste
                  automatiquement les champs spécifiques (budget JH vs total HT, paliers, Weekly CRA, etc.).
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Start <span className="text-red-600">*</span></Label><Input data-testid="edit-field-start" type="date" value={editForm.start_date || ""} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} /></div>
              <div><Label>End <span className="text-red-600">*</span></Label><Input data-testid="edit-field-end" type="date" value={editForm.end_date || ""} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} /></div>
            </div>
            {(editForm.type || m.type) !== "FORFAIT" && (
            <div>
              <Label>Budget (JH) <span className="text-red-600">*</span></Label>
              <Input data-testid="edit-field-budget" type="number" step="0.5" min="0" value={editForm.budget_jh ?? ""} onChange={(e) => setEditForm({ ...editForm, budget_jh: e.target.value })} />
              <div className="text-xs text-zinc-500 mt-1 font-mono">Currently assigned: {totalAssigned} JH</div>
            </div>
            )}
            {(editForm.type || m.type) === "FORFAIT" && canViewFinance && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 space-y-3" data-testid="edit-forfait-block">
                <div>
                  <Label>Total amount HT ({m.currency}) <span className="text-red-600">*</span></Label>
                  <Input
                    data-testid="edit-field-total-amount-ht"
                    type="number" min="0" step="0.01"
                    value={editForm.total_amount_ht ?? ""}
                    onChange={(e) => setEditForm({ ...editForm, total_amount_ht: e.target.value })}
                  />
                  <p className="text-[11px] text-zinc-500 mt-1">Editing the total automatically re-prices each milestone (percentages unchanged).</p>
                </div>
                <label className="flex items-start gap-2 text-sm" data-testid="edit-field-bdc-only-wrap">
                  <input
                    type="checkbox"
                    checked={!!editForm.bdc_only}
                    onChange={(e) => setEditForm({ ...editForm, bdc_only: e.target.checked })}
                    className="mt-0.5"
                    data-testid="edit-field-bdc-only"
                  />
                  <span className="leading-snug">
                    <strong>Bon de commande uniquement</strong>
                    <span className="text-zinc-600 text-xs block">
                      The client master contract (CONTRAT_CADRE) is not required to bill this mission — only the PO matters.
                    </span>
                  </span>
                </label>
              </div>
            )}
            <div>
              <Label>Country d'intervention <span className="text-red-600">*</span></Label>
              <Select value={editForm.country || ""} onValueChange={(v) => setEditForm({ ...editForm, country: v })}>
                <SelectTrigger data-testid="edit-field-country"><SelectValue placeholder="Select a country" /></SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}><span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                Drives the bank-info block on generated PDFs: <strong>Morocco</strong> uses the domestic CFG Bank block; any other country uses the SWIFT/CAIXA international correspondent block.
              </div>
            </div>
            {(user.role === "CEO" || user.role === "ADMIN") && (
              <div data-testid="edit-mission-manager-block">
                <Label>Mission manager <span className="text-red-600">*</span></Label>
                <Select
                  value={editForm.manager_id || ""}
                  onValueChange={(v) => setEditForm({ ...editForm, manager_id: v === "__SELF__" ? user.user_id : v })}
                >
                  <SelectTrigger data-testid="edit-field-manager"><SelectValue placeholder="Pick a manager" /></SelectTrigger>
                  <SelectContent>
                    {user.role === "CEO" && (
                      <SelectItem value="__SELF__" data-testid="edit-mission-manager-opt-self">
                        (Myself)
                      </SelectItem>
                    )}
                    {(() => {
                      // Build a unique list: assignableManagers + the current manager
                      // (in case they got suspended/role-changed since assignment, otherwise
                      // Radix Select shows an empty trigger). Dedup by user_id.
                      const byId = new Map();
                      for (const u of assignableManagers) byId.set(u.user_id, u);
                      if (editForm.manager_id && !byId.has(editForm.manager_id)) {
                        const cur = users.find((u) => u.user_id === editForm.manager_id);
                        if (cur) byId.set(cur.user_id, { ...cur, _legacy: true });
                      }
                      return Array.from(byId.values()).map((u) => (
                        <SelectItem key={u.user_id} value={u.user_id} data-testid={`edit-mission-manager-opt-${u.user_id}`}>
                          {u.name} <span className="text-zinc-500">({u.role}{u._legacy ? " · legacy" : ""})</span>
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  CEO / ADMIN only. Reassigning the manager triggers a notification to the new manager.
                </div>
              </div>
            )}
            <div className="border-t border-brand-cream-200 pt-3 mt-1">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Client-side contact (for invoice email) <span className="text-red-600">*</span></div>
              <ContactPicker clientId={editForm.client_id} value={editForm} testidPrefix="edit-contact"
                onApply={(c) => setEditForm((f) => ({ ...f, contact_name: c.contact_name || "", contact_email: c.contact_email || "", contact_gender: c.contact_gender || "" }))}>
              <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr_1fr] gap-3">
                <div>
                  <Label>Civility <span className="text-red-600">*</span></Label>
                  {/* Iter-198 — civilité requise pour envoyer toute correspondance client.
                      Sa valeur peuple {{contact_gender}} dans tous les templates. */}
                  <select
                    data-testid="edit-field-contact-gender"
                    value={editForm.contact_gender ?? ""}
                    onChange={(e) => {
                      const g = e.target.value;
                      setEditForm({ ...editForm, contact_gender: g, ...(g === "M./Mme" ? { contact_name: "" } : {}) });
                    }}
                    className="w-full h-10 border border-zinc-200 rounded-md px-2 text-sm bg-white"
                  >
                    <option value="">—</option>
                    <option value="M.">M.</option>
                    <option value="Mme.">Mme.</option>
                    <option value="M./Mme">M./Mme</option>
                  </select>
                </div>
                <div>
                  <Label>Contact name {editForm.contact_gender !== "M./Mme" && <span className="text-red-600">*</span>}</Label>
                  <Input
                    data-testid="edit-field-contact-name"
                    placeholder={editForm.contact_gender === "M./Mme" ? "Unknown contact — not used in emails" : "Jean Dupont"}
                    value={editForm.contact_name ?? ""}
                    disabled={editForm.contact_gender === "M./Mme"}
                    className={editForm.contact_gender === "M./Mme" ? "bg-zinc-100 text-zinc-400" : ""}
                    onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Contact email <span className="text-red-600">*</span></Label>
                  <Input
                    data-testid="edit-field-contact-email"
                    type="email"
                    placeholder="contact@client.com"
                    value={editForm.contact_email ?? ""}
                    onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  />
                </div>
              </div>
              </ContactPicker>
              <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                <strong>Civility + email</strong> are required to enable invoice / reminder sends.
                Pick <strong>M./Mme</strong> when the contact person is unknown (shared mailbox): the name is greyed out and emails start with "Bonjour M./Mme,".
                The civility is interpolated into every client-facing email via <code className="font-mono text-[10px]">{`{{contact_gender}}`}</code>.
              </div>
              {(() => {
                const cur = clients.find((cc) => cc.client_id === editForm.client_id);
                if (!cur?.is_subcontractor) return null;
                return (
                  <div className="mt-4 pt-4 border-t border-brand-cream-200" data-testid="edit-final-client-wrap">
                    <Label>Final client name <span className="text-xs text-zinc-500">(for CRA export PDF)</span></Label>
                    <Input
                      data-testid="edit-final-client-name"
                      value={editForm.final_client_name ?? ""}
                      onChange={(e) => setEditForm({ ...editForm, final_client_name: e.target.value })}
                      placeholder="End-customer name shown on the exported CRA (e.g. Société X)"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
                      <strong>{cur.name}</strong> is a subcontractor. The CRA PDF will use this name in the <strong>CLIENT</strong> cell and the right-hand signature box.
                    </p>
                  </div>
                );
              })()}

              {/* Iter-194j/w — Billing delay (months) + optional days override. CEO only. */}
              {user?.role === "CEO" && (
                <div className="mt-4 pt-4 border-t border-brand-cream-200" data-testid="edit-billing-delay-wrap">
                  <Label htmlFor="edit-billing-delay-months">Délai de facturation (paiement client)</Label>
                  <Select
                    value={String(editForm.billing_delay_months || 1)}
                    onValueChange={(v) => setEditForm({ ...editForm, billing_delay_months: parseInt(v, 10) })}
                  >
                    <SelectTrigger id="edit-billing-delay-months" data-testid="edit-field-billing-delay-months" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 mois (relances J+20 / J+25 / J+29)</SelectItem>
                      <SelectItem value="2">2 mois (relances J+50 / J+55 / J+59)</SelectItem>
                      <SelectItem value="3">3 mois (relances J+80 / J+85 / J+89)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                    Définit la date limite d'encaissement de chaque facture issue de cette mission et déclenche les emails de relance automatiques.
                  </p>
                  <div className="mt-2">
                    <Label htmlFor="edit-billing-delay-days" className="text-xs">Override en jours (optionnel)</Label>
                    <input
                      type="number"
                      id="edit-billing-delay-days"
                      min={1}
                      max={365}
                      value={editForm.billing_delay_days ?? ""}
                      onChange={(e) => setEditForm({ ...editForm, billing_delay_days: e.target.value })}
                      data-testid="edit-field-billing-delay-days"
                      placeholder="Vide = utilise le délai en mois ci-dessus"
                      className="mt-1 w-full max-w-xs h-9 rounded border border-brand-cream-200 px-2 text-sm"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                      Si renseigné, prime sur le délai en mois (ex. 45 jours, 75 jours). Pour des délais 30/60/90, laissez ce champ vide.
                    </p>
                  </div>
                </div>
              )}

              {/* Iter-203 — Weekly CRA opt-in. Iter-204 — REGIE-only (disabled in FORFAIT). */}
              {(user?.role === "CEO" || user?.role === "ADMIN" || (user?.role === "MANAGER" && m.manager_id === user?.user_id)) && (
                <div className="mt-4 pt-4 border-t border-brand-cream-200">
                  {(() => {
                    const effType = editForm.type || m.type;
                    const isForfait = effType === "FORFAIT";
                    return (
                      <div className={`rounded-lg border p-4 ${isForfait ? "border-zinc-200 bg-zinc-50 opacity-70" : (editForm.weekly_cra_enabled ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40")}`} data-testid="edit-weekly-cra-block">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <Label htmlFor="edit-weekly-cra-toggle" className="text-sm font-semibold flex items-center gap-2">
                              {!isForfait && !editForm.weekly_cra_enabled && <AlertTriangle size={14} className="text-amber-600" />}
                              CRA hebdomadaire (Weekly CRA)
                            </Label>
                            <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
                              Activez ce flux uniquement <strong>si le client demande explicitement</strong> un rapport
                              hebdomadaire. Une fois activé : les collaborateurs assignés à cette mission verront le bouton
                              "Send Weekly CRA" sur leur timesheet et recevront les relances du vendredi 14h.
                            </p>
                            {isForfait ? (
                              <p className="text-[11px] text-zinc-600 mt-2 italic" data-testid="edit-weekly-cra-forfait-note">
                                ⓘ Non disponible pour les missions Forfait — le CRA hebdomadaire n'a de sens qu'en facturation au temps passé (Régie).
                              </p>
                            ) : !editForm.weekly_cra_enabled && (
                              <p className="text-[11px] text-amber-700 mt-2 italic">
                                ⚠ Désactivé : aucun bouton ne s'affichera et la relance vendredi 14h ignorera cette mission.
                              </p>
                            )}
                          </div>
                          <Switch
                            id="edit-weekly-cra-toggle"
                            checked={!isForfait && !!editForm.weekly_cra_enabled}
                            disabled={isForfait}
                            onCheckedChange={(v) => setEditForm({ ...editForm, weekly_cra_enabled: !!v })}
                            data-testid="edit-field-weekly-cra-enabled"
                            title={isForfait ? "Disponible uniquement pour les missions en Régie" : ""}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Iter-137: auto-invoice toggle on the mission (tri-state) */}
              {(user?.role === "CEO" || user?.role === "ADMIN") && (
                <div className="mt-4 pt-4 border-t border-brand-cream-200" data-testid="edit-auto-invoice-wrap">
                  <Label>{m.type === "FORFAIT" ? "Auto-invoice (milestone validation)" : "End-of-month auto-invoice"}</Label>
                  <select
                    data-testid="edit-field-auto-invoice"
                    value={editForm.auto_invoice_enabled === null || editForm.auto_invoice_enabled === undefined ? "inherit" : (editForm.auto_invoice_enabled ? "on" : "off")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditForm({ ...editForm, auto_invoice_enabled: v === "inherit" ? null : v === "on" });
                    }}
                    className="mt-1 w-full h-10 px-3 text-sm border border-brand-cream-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-navy"
                  >
                    <option value="inherit">Inherit from client / global setting</option>
                    <option value="on">Force ON (override client/global)</option>
                    <option value="off">Force OFF (disable on this mission)</option>
                  </select>
                  <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
                    {m.type === "FORFAIT"
                      ? "When a milestone (palier) is validated, the platform generates and emails the invoice automatically — no CRA involved. Deliverables and extra documents attached to the milestone ride along on the email."
                      : "When every collaborator's CRA is approved and all safeguards pass (BC valid, client validated), the platform will generate and email the invoice automatically."}
                  </p>
                  {/* Iter-195q — Per-mission send-mode override.
                      Cascade: mission > client > global. Null = inherit.
                      Iter-195v — FORFAIT missions ARE always sent immediately:
                      a milestone is a discrete contractual event, so waiting
                      for end-of-month makes no business sense and would even
                      block client validation. The selector is hidden + a note
                      explains the forced behavior. */}
                  {user?.role === "CEO" && m.type === "FORFAIT" && (
                    <div className="mt-3 rounded-md border border-brand-cream-200 bg-brand-cream-50/40 px-3 py-2">
                      <Label className="text-xs">Send mode (timing)</Label>
                      <p
                        className="text-[11px] text-zinc-600 mt-1 leading-relaxed"
                        data-testid="edit-field-auto-invoice-send-mode-forfait-note"
                      >
                        Mission FORFAIT — la facture est générée et envoyée <b>immédiatement à la validation du palier</b> (aucun CRA requis). Les modes "fin de mois" et "à l'approbation du CRA" ne s'appliquent qu'aux missions en régie.
                      </p>
                    </div>
                  )}
                  {user?.role === "CEO" && m.type !== "FORFAIT" && (
                    <div className="mt-3">
                      <Label className="text-xs">Send mode (timing)</Label>
                      <select
                        data-testid="edit-field-auto-invoice-send-mode"
                        value={editForm.auto_invoice_send_mode || ""}
                        onChange={(e) => setEditForm({ ...editForm, auto_invoice_send_mode: e.target.value || null })}
                        className="mt-1 w-full h-9 px-3 text-sm border border-brand-cream-200 rounded-md bg-white"
                      >
                        <option value="">Inherit from client / global</option>
                        <option value="END_OF_MONTH">End of month (wait for last day)</option>
                        <option value="IMMEDIATE">Immediate (as soon as CRA approved)</option>
                      </select>
                      <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                        Override le timing global. Past months are always sent immediately regardless of this setting.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-brand-cream-200 shrink-0 bg-brand-paper">
            <button onClick={() => setEditOpen(false)} className="px-4 h-10 text-sm hover:bg-brand-cream-50 rounded-md">Cancel</button>
            <button onClick={saveEdit} data-testid="edit-save" className="px-4 h-10 bg-brand-navy text-brand-cream-50 text-sm rounded-md hover:bg-brand-navy-600">Save changes</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit role dialog */}
      <EditRoleDialog
        roleEdit={roleEdit}
        onChange={setRoleEdit}
        onClose={() => setRoleEdit(null)}
        onSave={saveRoleEdit}
        currency={m.currency}
        isForfait={m.type === "FORFAIT"}
        canViewFinance={canViewFinance}
      />

      {/* Edit assignment dialog */}
      <EditAssignmentDialog
        assignEdit={assignEdit}
        onChange={setAssignEdit}
        onClose={() => setAssignEdit(null)}
        onSave={saveAssignEdit}
      />
      {/* Iter-194m — Close mission dialog: shows the KPI report + blocking
          CRAs, lets the CEO force-close if necessary. */}
      <MissionCloseDialog
        open={closeDialogOpen}
        missionId={id}
        currentUserIsCEO={user?.role === "CEO"}
        onClose={() => setCloseDialogOpen(false)}
        onClosed={() => { setCloseDialogOpen(false); load(); }}
      />
      {/* Assign manager dialog */}
      <Dialog open={assignMgrOpen} onOpenChange={setAssignMgrOpen}>
        <DialogContent data-testid="assign-manager-dialog">
          <DialogHeader>
            <DialogTitle>Attribuer un manager</DialogTitle>
            <DialogDescription>Pick the manager who will own this mission. They can then configure roles and staffing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Manager</Label>
            <Select value={assignMgrPick} onValueChange={setAssignMgrPick}>
              <SelectTrigger data-testid="assign-manager-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
              <SelectContent>
                {users
                  .filter((u) => ["MANAGER", "CEO", "ADMIN"].includes(u.role))
                  .map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id} data-testid={`assign-mgr-opt-${u.user_id}`}>
                      {u.name} <span className="text-xs text-zinc-500 font-mono">· {u.role}</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <button onClick={() => setAssignMgrOpen(false)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={saveAssignManager} data-testid="assign-manager-save" className="px-4 h-10 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600">Attribuer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-56: CEO Drive folder URL prompt at activation time */}
      <Dialog open={driveValidateOpen} onOpenChange={setDriveValidateOpen}>
        <DialogContent data-testid="validate-drive-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Shared Google Drive folder</DialogTitle>
            <DialogDescription>
              Before activating this mission, paste the URL of the shared Drive folder where the
              purchase orders (bons de commande) will be uploaded by the CEO, gestionnaire or manager.
              You can skip and set it later from the mission page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Drive folder URL</Label>
            <Input
              data-testid="validate-drive-input"
              type="url"
              placeholder="https://drive.google.com/drive/folders/..."
              value={driveValidateUrl}
              onChange={(e) => setDriveValidateUrl(e.target.value)}
              autoFocus
            />
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              The URL becomes editable later from the mission detail page (CEO only).
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={skipDriveAndValidate}
              data-testid="validate-drive-skip"
              className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md"
            >
              Skip & activate
            </button>
            <button
              onClick={confirmDriveAndValidate}
              disabled={driveValidateSaving}
              data-testid="validate-drive-save"
              className="px-4 h-10 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
            >
              {driveValidateSaving ? "Activating…" : "Save & activate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// CEO-only inline editor for the mission's Shared Google Drive folder URL.
// This is the folder where the mission's purchase orders (bons de commande) get
// uploaded. Hidden in the regular Edit Mission dialog — only the CEO can set it.
// ============================================================================
function CeoDriveFolderEditor({ missionId, initialUrl, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialUrl || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(initialUrl || "");
  }, [initialUrl]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/missions/${missionId}`, { drive_folder_url: value.trim() });
      toast.success("Drive folder URL saved");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid="ceo-drive-folder-edit-btn"
        className="inline-flex items-center gap-2 mt-2 text-[11px] uppercase tracking-[0.18em] font-mono text-brand-navy-500 hover:text-brand-navy"
      >
        <Pencil size={11} />
        {initialUrl ? "Edit shared Drive folder URL (CEO)" : "Set shared Drive folder URL (CEO)"}
      </button>
    );
  }
  return (
    <div className="mt-3 p-3 border border-brand-navy/15 rounded-md bg-brand-cream-50/60 max-w-2xl" data-testid="ceo-drive-folder-editor">
      <Label className="text-[11px] uppercase tracking-[0.18em] font-mono text-brand-navy-500">
        Shared Google Drive folder (purchase orders)
      </Label>
      <Input
        data-testid="ceo-drive-folder-input"
        type="url"
        placeholder="https://drive.google.com/drive/folders/..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="mt-2"
      />
      <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
        URL of the mission's shared Drive folder. The bon de commande will be uploaded here when added.
      </p>
      <div className="flex gap-2 justify-end mt-3">
        <button
          type="button"
          onClick={() => { setValue(initialUrl || ""); setEditing(false); }}
          className="px-3 h-9 text-xs hover:bg-brand-cream-200 rounded-md"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="ceo-drive-folder-save"
          className="px-3 h-9 bg-brand-navy text-white text-xs rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Iter-228w — Per-mission bank account override.
// CEO can pin a specific bank account on this mission (or clear it back to
// the currency-level default). The change applies to FRESHLY generated
// invoices; previously rendered PDFs keep the bank info baked at issue time.
// ──────────────────────────────────────────────────────────────────────────
function MissionBankAccountOverride({ missionId, currency, currentValue, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [val, setVal] = useState(currentValue || "");
  const [saving, setSaving] = useState(false);
  const [resolved, setResolved] = useState(null);

  useEffect(() => { setVal(currentValue || ""); }, [currentValue]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/bank-accounts");
        setAccounts(r.data?.items || []);
      } catch { /* swallow — non-blocking display */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/bank-accounts/_resolve", {
          params: { currency, mission_id: missionId },
        });
        setResolved(r.data?.resolved || null);
      } catch { /* swallow */ }
    })();
  }, [currency, missionId, val]);

  const save = async () => {
    setSaving(true);
    try {
      // Empty string = clear override (backend converts "" to null).
      await api.patch(`/missions/${missionId}`, { bank_account_id: val || "" });
      toast.success("Compte bancaire mis à jour pour cette mission");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 p-3 border border-zinc-200 rounded-md bg-zinc-50/50 max-w-2xl"
         data-testid="mission-bank-account-override">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
        Coordonnées bancaires (sur les factures)
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={val}
          onChange={(e) => setVal(e.target.value)}
          data-testid="mission-bank-account-select"
          className="text-sm border border-zinc-300 rounded px-2 h-8 min-w-[260px]"
        >
          <option value="">— Défaut (selon la devise : {currency}) —</option>
          {accounts.map((a) => (
            <option key={a.bank_account_id} value={a.bank_account_id}>
              [{a.scope}] {a.label}{a.currency ? ` · ${a.currency}` : ""}
              {a.is_default ? " (défaut)" : ""}
            </option>
          ))}
        </select>
        <button
          onClick={save}
          disabled={saving || val === (currentValue || "")}
          data-testid="mission-bank-account-save"
          className="inline-flex items-center gap-1 px-3 h-8 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800 disabled:opacity-50"
        >
          {saving ? "…" : "Enregistrer"}
        </button>
      </div>
      {resolved && (
        <div className="text-[11px] text-zinc-500 mt-2 font-mono">
          → Sera rendu sur les nouvelles factures : <span className="text-zinc-800 font-semibold">{resolved.label}</span>
          {resolved.scope === "DEVISE" && resolved.via_correspondent && (
            <span> · via correspondant {resolved.correspondent_bank_name}</span>
          )}
        </div>
      )}
    </div>
  );
}

